import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { Button, buttonClasses } from '@/design-system/Button'
import { LanguageSwitcher } from '@/design-system/LanguageSwitcher'
import { Reveal } from '@/design-system/motion'
import { Container, Overline, Skeleton } from '@/design-system/primitives'
import { PublicLayout } from '@/layouts/PublicLayout'
import { ApiError } from '@/lib/apiClient'
import { applyEventTheme } from '@/lib/theme'

import { useEventContent } from '../api'
import { siteText } from '../siteTexts'
import type { PublicEvent } from '../types'
import { useRubrics } from '../useRubrics'

interface RubricPageProps {
  /** Titre affiché et repris dans l'onglet du navigateur. */
  title: string
  /** Introduction de secours, tant que le bloc `introKey` n'existe pas. */
  intro?: string
  /**
   * Le bloc `site.*` qui porte l'introduction, réécrivable dans Contenus.
   * Présent, il l'emporte sur `intro` — vide, il n'y a pas d'introduction.
   */
  introKey?: string
  children: (event: PublicEvent) => React.ReactNode
}

/**
 * La coquille d'une page de rubrique.
 *
 * Chargement, édition non publiée, API injoignable, thème de l'édition, titre
 * d'onglet, barre de navigation : traités une fois ici. Sans cela, chacune des
 * cinq rubriques rejouerait la même trentaine de lignes — et l'une d'elles
 * finirait par diverger sur l'état d'erreur, celui qu'on teste le moins.
 */
export function RubricPage({ title, intro, introKey, children }: RubricPageProps) {
  const { t } = useTranslation()
  const { eventQuery, event, navItems } = useRubrics()
  // Partagé avec les pages qui lisent aussi les blocs : une seule requête.
  const content = useEventContent(introKey ? event?.slug : undefined)
  const written = introKey ? siteText(content.data, introKey) : undefined
  const shownIntro = written ? written.body : intro

  useEffect(() => {
    if (event) applyEventTheme(event.theme)
  }, [event])

  useEffect(() => {
    if (event) document.title = `${title} — ${event.name}`
  }, [event, title])

  if (eventQuery.isPending) {
    return (
      <Container className="flex min-h-dvh flex-col justify-center gap-6 py-24">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-16 w-full max-w-xl" />
        <Skeleton className="h-4 w-64" />
        <span className="sr-only">{t('state.loading')}</span>
      </Container>
    )
  }

  if (eventQuery.isError || !event) {
    const notPublished =
      eventQuery.error instanceof ApiError && eventQuery.error.code === 'EVENT_NOT_PUBLISHED'

    return (
      <Container className="flex min-h-dvh flex-col justify-center py-24">
        {/* Même sans édition, la page doit se lire dans sa langue. */}
        <div className="mb-10 flex items-center justify-between gap-4">
          <img src="/leyamo.png" alt="LeYAMO" className="h-14 w-14 rounded-full" />
          <LanguageSwitcher />
        </div>
        <h1 className="font-display text-display-lg text-content-primary">
          {notPublished ? t('state.noEventTitle') : t('state.errorTitle')}
        </h1>
        <p className="mt-4 max-w-xl text-body-lg text-content-secondary">
          {notPublished ? t('state.noEventBody') : t('state.errorBody')}
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          {!notPublished ? (
            <Button onClick={() => void eventQuery.refetch()}>{t('state.retry')}</Button>
          ) : null}
          <Link to="/" className={buttonClasses({ variant: 'outline' })}>
            {t('nav.home')}
          </Link>
        </div>
      </Container>
    )
  }

  return (
    <PublicLayout
      eventName={event.name}
      logoUrl={event.logo_url}
      availableLocales={event.locales}
      navItems={navItems}
    >
      {/* L'aurore reprend derrière l'en-tête : les pages de rubriques
          appartiennent visiblement au même lieu que l'accueil. */}
      <header className="relative isolate overflow-hidden pt-24 sm:pt-28">
        <div aria-hidden className="aurora -z-10 opacity-40" />
        <Container>
          <Reveal>
            <Overline>{event.name}</Overline>
            <h1 className="mt-3 font-display text-display-md text-content-primary">{title}</h1>
            {shownIntro ? (
              <p className="mt-4 max-w-2xl whitespace-pre-line text-body-lg text-content-secondary">
                {shownIntro}
              </p>
            ) : null}
          </Reveal>
        </Container>
      </header>

      {children(event)}

      {/* Espace réservé à la barre d'achat collante du mobile. */}
      <div aria-hidden className="h-16 sm:hidden" />
    </PublicLayout>
  )
}
